import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TlsContextInfo } from '@sbclite/core';
import { api } from '../services/api';

export const tlsContextQueryKeys = {
  all: ['tls-contexts'] as const,
  inventory: ['tls-contexts', 'inventory'] as const,
  item: (id: string) => ['tls-contexts', id] as const,
} as const;

export interface TlsContextInventoryRow {
  id: string;
  name: string;
  scope: 'sip_interface' | 'sip_peer';
  enabled: boolean;
  tlsContextId: string | null;
  status: 'migrated' | 'backfillable' | 'blocked';
  desiredContextName: string | null;
  blockers: ReadonlyArray<{ code: string; message: string }>;
}

export interface TlsContextInventoryAnalysis extends TlsContextInventoryRow {
  blockers: ReadonlyArray<{ code: string; message: string }>;
}

export interface TlsContextInventorySummary {
  totals: { all: number; enabled: number };
  migrated: number;
  backfillable: number;
  blocked: number;
  readyForCleanup: boolean;
  backfillTargets: ReadonlyArray<TlsContextInventoryAnalysis>;
  blockers: ReadonlyArray<TlsContextInventoryAnalysis>;
}

export interface TlsContextInventoryResponse {
  rows: TlsContextInventoryRow[];
  summary: TlsContextInventorySummary;
}

export interface TlsContextBackfillResponse {
  dryRun: boolean;
  appliedCount: number;
  before: TlsContextInventorySummary;
  after: TlsContextInventorySummary;
  cleanupReady: boolean;
}

export function useTlsContexts() {
  return useQuery({
    queryKey: tlsContextQueryKeys.all,
    queryFn: async (): Promise<TlsContextInfo[]> => {
      const response = await api.getTlsContexts();
      return response.data;
    },
  });
}

export function useTlsContext(id: string | null | undefined) {
  return useQuery({
    queryKey: tlsContextQueryKeys.item(id ?? ''),
    queryFn: async (): Promise<TlsContextInfo | null> => {
      if (!id) return null;
      const response = await api.getTlsContext(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateTlsContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: unknown) => api.createTlsContext(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.inventory });
    },
  });
}

export function useUpdateTlsContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: unknown }) => api.updateTlsContext(id, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.inventory });
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.item(variables.id) });
    },
  });
}

export function useDeleteTlsContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => api.deleteTlsContext(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.inventory });
    },
  });
}

export function useTlsContextInventory() {
  return useQuery({
    queryKey: tlsContextQueryKeys.inventory,
    queryFn: async (): Promise<TlsContextInventoryResponse> => {
      const response = await api.getTlsContextInventory();
      return response.data;
    },
  });
}

export function useRunTlsContextBackfill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (apply: boolean) => {
      const response = await api.runTlsContextBackfill(apply);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.inventory });
      queryClient.invalidateQueries({ queryKey: tlsContextQueryKeys.all });
    },
  });
}
